import { useDispatch, useSelector } from 'react-redux';
import useAddToCart from '../../../hooks/useAddToCart';
import { MonthlyDeclarationOverviewItem } from '../../../@types/TaxProfile';
import { setShowModalCheckout } from '../../../redux/slices/plans.slice';
import { REGULARIZATION_REGARATION_PRODUCTS_SLUG } from '../../../shared/constants/SlugsProducts';
import { RootState } from '../../../redux/store';
import { CircleNotch } from '@phosphor-icons/react';

const PurchaseDeclaration = ({
	selectedDeclarations,
	setSelectedDeclarations,
	type = 'monthly',
	resetOrders = true,
	variant = 'green',
}: {
	selectedDeclarations: MonthlyDeclarationOverviewItem[];
	setSelectedDeclarations: (
		declarations: MonthlyDeclarationOverviewItem[],
	) => void;
	regimen?: REGULARIZATION_REGARATION_PRODUCTS_SLUG;
	type?: string;
	resetOrders?: boolean;
	variant?: 'green' | 'blue';
}) => {
	const dispatch = useDispatch();
	const { handleAddCustomProduct, isLoading: isLoadingAddToCart } = useAddToCart();
	const { products } = useSelector((state: RootState) => state.productList);
	const isSidebarExpanded = useSelector(
		(state: RootState) => state.ui.sidebarIsExpanded,
	);

	const regularizationProducts = products?.find(
		(product) => product.name === 'Declaración Atrasada',
	)?.products;

	const getProductVersionByType = (type: string) => {
		const product = regularizationProducts?.find(
			(product) =>
				product.slug ===
				{
					resico: REGULARIZATION_REGARATION_PRODUCTS_SLUG.REG_RESICO_PLUS,
					platec:
						REGULARIZATION_REGARATION_PRODUCTS_SLUG.REG_PT_TODAS_ACTIVIDADES,
				}[type],
		);

		return product?.default_product_version_id;
	};

	const getRegimeByType = (type: 'platec' | 'resico') => {
		const regimesFFT = {
			platec: 'PLA-TEC',
			resico: 'RESICO',
		}[type];
		return [regimesFFT];
	};

	const handleAddToCart = async () => {
		let payload: {
			product_version_id?: number;
			additional_information: {
				regimes: string[];
				month?: number;
				year: number;
				product_package?: string;
			};
		}[] = [];
		if (type === 'annual') {
			payload = selectedDeclarations.map((declaration) => ({
				product_version_id: 45,
				additional_information: {
					regimes: [],
					year: Number(declaration.declaration_year),
				},
			}));
		} else {
			payload = selectedDeclarations
				?.filter(
					(item) => item?.type && ['platec', 'resico'].includes(item.type),
				)
				?.map((declaration) => {
					return {
						product_version_id: getProductVersionByType(
							declaration.type as 'platec' | 'resico',
						),
						additional_information: {
							regimes: getRegimeByType(declaration.type as 'platec' | 'resico'),
							month: Number(declaration.declaration_period?.split('-')[0]),
							year: Number(declaration.declaration_period?.split('-')[1]),
						},
					};
				});
		}

		const response = await handleAddCustomProduct({
			payload,
			navigateToCart: true,
			resetOrders,
		});
		if (!response) return;
		dispatch(setShowModalCheckout(true));
		setSelectedDeclarations([]);
	};

	const label = `Contratar ${selectedDeclarations.length} ${selectedDeclarations.length === 1 ? 'declaración' : 'declaraciones'} ${selectedDeclarations.length > 1 ? 'atrasadas' : ''}`;

	const labelDiscount = (() => {
		if (selectedDeclarations.length < 2 || !selectedDeclarations) return null;
		if (selectedDeclarations.length <= 6) return '20% de descuento';
		if (selectedDeclarations.length <= 12) return '30% de descuento';
		if (selectedDeclarations.length <= 24) return '40% de descuento';
		return '50% de descuento';
	})();

	if (selectedDeclarations.length === 0) return null;

	return (
		<div>
			<div
				className={`fixed bottom-12 md:bottom-0 left-0 right-0 bg-white p-4 max-w-[620px] w-full mx-auto shadow-lg border border-neutral-200 rounded-t-2xl fade-in-scale transition-all z-10 ${
					isSidebarExpanded ? 'md:left-[290px]' : 'md:left-[90px]'
				}`}
			>
				<button
					disabled={isLoadingAddToCart?.products}
					className={`flex-col md:flex-row min-h-[48px] rounded-xl text-white px-4 py-2 flex items-center justify-center w-full gap-1 ${
						isLoadingAddToCart?.products && 'opacity-70'
					} ${variant === 'green' ? 'bg-green-700' : 'bg-h-primary'}`}
					onClick={handleAddToCart}
				>
					<span className='flex items-center gap-1'>
						{isLoadingAddToCart?.products && (
							<CircleNotch className='animate-spin' />
						)}
						{label}
					</span>
					{labelDiscount && (
						<span className='bg-white/15 rounded-full px-2 py-1 text-sm'>
							{labelDiscount}
						</span>
					)}
				</button>
			</div>
		</div>
	);
};

export default PurchaseDeclaration;
